import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

interface Props {
  src: string;
  title: string;
  onClose: () => void;
}

/**
 * Full-screen look at one image: the card the operator clicked in a table.
 *
 * A third sibling of Modal and Drawer rather than a Modal variant, for the same
 * reason Drawer is one — those two share z-40/z-50, and this opens *from inside*
 * the add-cards drawer, so it has to sit above a z-50 panel (z-[60] backdrop,
 * z-[70] image) and render through a portal so the drawer's own overflow
 * clipping never crops it.
 *
 * Escape is caught in the capture phase and stopped there: the drawer listens
 * for Escape on the same document node in the bubble phase, and one keypress
 * must close the image the operator is looking at, not the picker behind it.
 */
export function ImageLightbox({ src, title, onClose }: Props) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onClose();
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [onClose]);

  return createPortal(
    <>
      <div className="fixed inset-0 z-[60] bg-black/70" onClick={onClose} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="fixed inset-0 z-[70] flex items-center justify-center p-6"
        onClick={onClose}
      >
        <img
          src={src}
          alt={title}
          onClick={(e) => e.stopPropagation()}
          className="max-h-[88vh] max-w-[88vw] rounded-lg object-contain shadow-2xl"
        />
        <button
          type="button"
          onClick={(e) => {
            // The frame closes on any click that misses the image; without this
            // the button's own click would close it a second time.
            e.stopPropagation();
            onClose();
          }}
          aria-label="Close"
          className="absolute right-4 top-4 rounded-full bg-black/50 p-2 text-white transition-colors hover:bg-black/70"
        >
          <X size={18} aria-hidden="true" />
        </button>
      </div>
    </>,
    document.body,
  );
}
